import {
  Backdrop,
  Badge,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Fade,
  IconButton,
  Slide,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import React, { useRef, useState } from "react"
import { theme } from "themes/default/theme"
import CloseIcon from "@mui/icons-material/Close"
import AttachFileIcon from "@mui/icons-material/AttachFile"
import { useTranslation } from "react-i18next"
import { styles } from "./SomethingWrongModal.styles"
import Image from "components/Image"
import { useRect } from "@reactour/utils"
import GetStarted from "components/GetStarted"
import { nextStyles } from "components/ActivityContent/Activity.GetStarted"
import EastIcon from "@mui/icons-material/East"
import TextToolBar from "components/ActivityContent/TextComprehesion/TextToolBar"
const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />
})

/**
 *
 * @param {int} sequence sequence can be 0 - none, 1 - content error, 2 - glitch in the system
 * @returns
 */
export default function SomethingWrongModal({
  open,
  onClose,
  onSubmit,
  loadingScreenshot = true,
  sequence = 0,
}) {
  const { t } = useTranslation()
  const [value, setValue] = React.useState("")
  const [attachedImage, setAttachedImage] = React.useState(null)
  const [isGetStartedOpen, setIsGetStartedOpen] = React.useState(false)
  const inputRef = useRef()
  const buttonRef = useRef()
  const inputSizes = useRect(inputRef)
  const buttonSizes = useRect(buttonRef)
  const [fontSettings, setFontSettings] = useState({
    fontSize: undefined,
    fontFamily: undefined,
    bgColor: undefined,
  })

  React.useEffect(() => {
    if (!open) {
      setValue("")
      setAttachedImage(null)
      setIsGetStartedOpen(false)
    }
  }, [open])

  const openAttachment = () => {
    // attach image file
    setIsGetStartedOpen(false)
    const input = document.createElement("input")
    input.type = "file"
    input.accept = "image/*"
    input.onchange = handleImageChange
    input.click()
  }

  const removeAttachment = () => {
    setAttachedImage(null)
  }

  const handleImageChange = (event) => {
    const selectedImage = URL.createObjectURL(event.target.files[0])
    setAttachedImage(selectedImage)
  }

  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={Transition}
      fullWidth
      PaperProps={{
        sx: [
          styles.paper,
          fontSettings.fontFamily && {
            "& .MuiTypography-root, button, textarea": {
              fontFamily: fontSettings.fontFamily,
            },
          },
        ],
      }}
    >
      <Box display={"none"}>
        <TextToolBar
          updateFontStyle={(fontFamily) =>
            setFontSettings((prev) => ({ ...prev, fontFamily }))
          }
          updateFontSize={(fontSize) =>
            setFontSettings((prev) => ({ ...prev, fontSize }))
          }
          updateBgColor={(bgColor) =>
            setFontSettings((prev) => ({ ...prev, bgColor }))
          }
        />
      </Box>
      <IconButton aria-label="close" onClick={onClose} sx={styles.closeButton}>
        <CloseIcon fontSize="large" />
      </IconButton>
      <DialogTitle
        textAlign={"center"}
        color={theme.palette.common.white}
        fontSize={32}
        sx={styles.title}
      >
        {sequence === 1
          ? t("content_error_whats_going_on")
          : t("technical_support_whats_going_on")}
      </DialogTitle>
      <DialogContent sx={styles.content}>
        <Stack
          sx={styles.top}
          direction="row"
          alignItems="center"
          justifyContent={"space-between"}
        >
          <Typography color={theme.palette.common.white} variant="span">
            {`${value.length}/1500`}
          </Typography>
          <Stack direction={"row"} alignItems={"center"} mt={1.5} spacing={1}>
            {attachedImage && attachedImage !== null && (
              <Fade in>
                <Badge
                  badgeContent={
                    <CloseIcon
                      sx={styles.removeIcon}
                      onClick={removeAttachment}
                    />
                  }
                >
                  <IconButton
                    sx={styles.removeButton}
                    onClick={removeAttachment}
                  >
                    <Image name="pictures" width="2rem" height="2rem" />
                  </IconButton>
                </Badge>
              </Fade>
            )}
            {isGetStartedOpen && (
              <GetStarted sizes={buttonSizes} position="right">
                <Stack>
                  <Typography>{t("you_can_add_attachment")}</Typography>
                  <Button
                    variant="contained"
                    color="primary"
                    disableElevation
                    onClick={() => setIsGetStartedOpen(false)}
                    sx={nextStyles}
                  >
                    <EastIcon />
                  </Button>
                </Stack>
              </GetStarted>
            )}
            <IconButton
              sx={[styles.icon, isGetStartedOpen && styles.tourOpen]}
              onClick={openAttachment}
              className="attachemnt-button"
              ref={buttonRef}
            >
              <AttachFileIcon />
            </IconButton>
          </Stack>
        </Stack>
        {isGetStartedOpen && (
          <GetStarted sizes={inputSizes} position="left">
            <Stack>
              <Typography>
                {sequence === 1
                  ? t("let_us_know_what_is_the_content_error_you_found")
                  : t("let_us_know_what_is_the_bug_you_found")}
              </Typography>
            </Stack>
          </GetStarted>
        )}
        <TextField
          type="text"
          fullWidth
          multiline
          minRows={8}
          maxRows={8}
          value={value}
          autoFocus
          onChange={(e) => {
            setValue(e.target.value)
            setIsGetStartedOpen(false)
          }}
          sx={[styles.input, isGetStartedOpen && styles.inputTour]}
          inputProps={{ maxLength: 1500 }}
          className="something-wrong-input"
          inputRef={inputRef}
        />
      </DialogContent>
      <DialogActions sx={styles.actions}>
        <Typography mr={2} variant={"span"} color={theme.palette.common.white}>
          {loadingScreenshot ? t("loading") : t("loading_succesful")}
        </Typography>
        <Button
          variant="contained"
          color="success"
          disableElevation
          disabled={loadingScreenshot}
          onClick={() => {
            if (value === "" && attachedImage === null) {
              return
            }
            onSubmit({
              description: value,
              attachedImage,
            })
          }}
          sx={styles.button}
        >
          {t("submit_ticket")}
        </Button>
        <Backdrop open={isGetStartedOpen} />
      </DialogActions>
    </Dialog>
  )
}
